import React, { useState, useRef } from 'react';
import { useUserResumes, useUploadResume } from '../../hooks/unified/useResumeQueries';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useToast } from '../../contexts/ToastContext';

interface ResumeSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResumeSelected: (resumeId: string) => void;
  jobId: string;
  jobTitle: string;
}

export const ResumeSelectionModal: React.FC<ResumeSelectionModalProps> = ({
  isOpen,
  onClose,
  onResumeSelected,
  jobId: _jobId, // Prefix with underscore to indicate intentionally unused
  jobTitle,
}) => {
  // Fetch user resumes
  const { data: resumes, isLoading } = useUserResumes();
  const uploadResumeMutation = useUploadResume();
  const { showSuccess, showError } = useToast();

  // Selection state
  const [selectedResumeId, setSelectedResumeId] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  
  // Upload state
  const [uploadLabel, setUploadLabel] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  // Handle upload
  const handleUpload = async (file: File) => {
    // Validate file type
    if (file.type !== 'application/pdf') {
      showError('Invalid File Type', 'Only PDF files are accepted.');
      return;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      showError('File Too Large', 'File size must be less than 10MB.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const result = await uploadResumeMutation.mutateAsync({
        file,
        label: uploadLabel || undefined,
        onProgress: (progress) => {
          setUploadProgress(progress);
        },
      });

      showSuccess('Resume Uploaded', 'Your resume has been uploaded successfully.');
      
      // Auto-select the newly uploaded resume
      setSelectedResumeId(result.id);
      setShowUploadForm(false);
      setUploadLabel('');
      setUploadProgress(0);
    } catch (err: any) {
      showError('Upload Failed', err.message || 'Failed to upload resume. Please try again.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Trigger file input
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Select Resume for Application
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Applying for: <span className="font-medium text-gray-900">{jobTitle}</span>
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="md" />
            </div>
          )}

          {/* Resume List */}
          {!isLoading && resumes && resumes.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700 mb-4">
                Select a resume to submit with your application:
              </p>
              
              {/* Upload New Resume Option */}
              <label
                className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                  showUploadForm
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="resume"
                  value="upload-new"
                  checked={showUploadForm}
                  onChange={() => setShowUploadForm(true)}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-gray-900">
                    Upload New Resume
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Upload a new resume for this application
                  </p>
                </div>
              </label>

              {/* Upload Form */}
              {showUploadForm && (
                <div className="ml-7 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Label (optional)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., Technical Resume"
                        value={uploadLabel}
                        onChange={(e) => setUploadLabel(e.target.value)}
                        disabled={isUploading}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <button
                        onClick={handleUploadClick}
                        disabled={isUploading}
                        className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUploading ? (
                          <>
                            <LoadingSpinner size="sm" className="mr-2" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <svg
                              className="mr-2 h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                              />
                            </svg>
                            Choose PDF File
                          </>
                        )}
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </div>

                    {/* Upload Progress */}
                    {isUploading && uploadProgress > 0 && (
                      <div>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span>Uploading...</span>
                          <span>{uploadProgress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="border-t border-gray-200 my-4"></div>
              
              {resumes.map((resume) => (
                <label
                  key={resume.id}
                  className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedResumeId === resume.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="resume"
                    value={resume.id}
                    checked={selectedResumeId === resume.id}
                    onChange={() => {
                      setSelectedResumeId(resume.id);
                      setShowUploadForm(false);
                    }}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-900">
                        {resume.filename}
                      </h3>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          resume.status === 'scanned'
                            ? 'bg-green-100 text-green-800'
                            : resume.status === 'processing'
                            ? 'bg-blue-100 text-blue-800'
                            : resume.status === 'error'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {resume.status}
                      </span>
                    </div>
                    {resume.label && (
                      <p className="text-sm text-gray-600 mt-1">
                        {resume.label}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Uploaded: {resume.submitted_at ? formatDate(resume.submitted_at) : 'N/A'}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Empty State - Show Upload Form */}
          {!isLoading && (!resumes || resumes.length === 0) && (
            <div className="space-y-4">
              <div className="text-center py-4">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No resumes</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Upload a resume to apply for this job.
                </p>
              </div>

              {/* Upload Form */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Label (optional)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Technical Resume"
                      value={uploadLabel}
                      onChange={(e) => setUploadLabel(e.target.value)}
                      disabled={isUploading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <button
                      onClick={handleUploadClick}
                      disabled={isUploading}
                      className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUploading ? (
                        <>
                          <LoadingSpinner size="sm" className="mr-2" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <svg
                            className="mr-2 h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                            />
                          </svg>
                          Choose PDF File
                        </>
                      )}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>

                  {/* Upload Progress */}
                  {isUploading && uploadProgress > 0 && (
                    <div>
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span>Uploading...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              disabled={!selectedResumeId || isUploading}
              onClick={() => {
                if (selectedResumeId) {
                  onResumeSelected(selectedResumeId);
                  onClose();
                }
              }}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
